// React
import { useState, useEffect } from "react";

// OpenLayers
import OlMap from "ol/Map";
import { getPointResolution, transformExtent } from "ol/proj";

// Other
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import $ from "jquery";

// Classes
import OpusLayer from "lib/classes/opuslayer";
import ExportedLegend from "lib/classes/exportedlegend";
import Vector2 from "lib/classes/vector2";
import { Popup, PopupProps } from "lib/classes/popup";

// Tools
import { makeUrl } from "lib/utilities/misc";
import { fetchJson } from "lib/utilities/network";
import { assetURL } from "lib/utilities/asset";
import { getMapCoords, getAllLayerCodesVisible, getOpusLayerByCode } from "lib/utilities/map";
import { toPrecision } from "lib/utilities/maths";
import { idify, dateTimeString, updateURLParameter, limitString, forEachManual } from "lib/utilities/misc";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Styles
import "styles/css/Print.scss";

const dims:any = {
	"a0": [1189, 841],
	"a1": [841, 594],
	"a2": [594, 420],
	"a3": [420, 297],
	"a4": [297, 210],
	"a5": [210, 148]
};
const exportOptions:any = {};
const div:number = 25.4;
const markerIconSource:string = "assets/images/marker.png";

// Page margins
const marginX:number = 16;
const marginY:number = 10;
const footerHeight:number = 55;

const PrintPopup = new Popup({
	title: () => getLocaleString("print"),
	render: function() {
		const olMap:OlMap = this.get("map");
		const mapConfig:any = olMap.get("opus-config");
		
		// State hooks
		const [pageSize, setPageSize] = useState<string>("a4");
		const [layerLayout, setLayerLayout] = useState<string>("small");
		const [searchResults, setSearchResults] = useState<any[]>([]);
		
		// Initialize
		useEffect(() => {
			const handleLayers = (layers:OpusLayer[], results:any) => {
				results = results || [];
				
				results.forEach((result:any) => {
					result.shouldPrint = true;
				});
				
				setSearchResults(results);
			};
			
			_events.invoke("getPinDropLayers", handleLayers);
			_events.on("pin-drop-layers", handleLayers);
		}, []);
			
		useEffect(() => {
			if(pageSize == "a5" && layerLayout == "small")
				setLayerLayout("large");
		}, [pageSize]);
		
		// Rebuild tooltips when pin drop layers change
		useEffect(() => {
			_ui.tooltip.rebuild();
		}, [searchResults]);
		
		const exportPDF = (shouldPrint:boolean) => {
			const dim:Array<number> = dims[pageSize];
			const canvasWidth:number = $("#ol-map-container").width();
			const canvasHeight:number = $("#ol-map-container").height();
			const canvasRatio:number = canvasWidth / canvasHeight;
			
			_ui.showLoading();
			
			olMap.render();
			
			olMap.once("rendercomplete", () => {
				exportOptions.width = canvasWidth;
				exportOptions.height = canvasHeight;
				
				html2canvas(olMap.getViewport(), exportOptions)
				.then((canvas:HTMLCanvasElement) => {
					const pdf:jsPDF = new jsPDF("landscape", undefined, pageSize);
					
					const pdfWidth:number = pdf.internal.pageSize.getWidth();
					const pdfHeight:number = pdf.internal.pageSize.getHeight();
					
					const containerWidth:number = pdfWidth - (marginX * 2);
					const containerHeight:number = pdfHeight - footerHeight;
					
					let imageWidth:number = containerWidth;
					let imageHeight:number = containerWidth / canvasRatio;
					
					if(imageHeight > containerHeight) {
						imageHeight = containerHeight;
						imageWidth = imageHeight * canvasRatio;
					}
						
					const middleX:number = (pdfWidth / 2) - (imageWidth / 2);
					const middleY:number = (pdfHeight / 2) - (imageHeight / 2);
					
					const x:number = middleX;
					const y:number = marginY;
					
					// <Add text>
						const textX:number = marginX+5;
						const mapNameY:number = y + containerHeight + marginY;
						
						pdf.setFontSize(16);
						pdf.setFont(undefined, "bold");
						pdf.text(
							limitString(mapConfig.name, 40),
							textX,
							mapNameY
						);
						
						const timeLabelY:number = mapNameY + 6;
						
						pdf.setFontSize(10);
						pdf.setFont(undefined, "normal");
						pdf.text(
							dateTimeString(),
							textX,
							timeLabelY 
						);
						
						const coordinatesLabelY:number = timeLabelY + 5;
						
						pdf.setFontSize(10);
						pdf.setFont(undefined, "normal");
						pdf.text(
							getMapInfoString(),
							textX,
							coordinatesLabelY 
						);
						
						const attributionY:number = coordinatesLabelY + 5;
						
						pdf.setFontSize(10);
						pdf.text(
							limitString(mapConfig.attribution, 55),
							textX,
							attributionY 
						);
						
						const layerAttributionY:number = attributionY + 5;
						
						pdf.setFontSize(8);
						pdf.text(
							limitString(olMap.get("active-layer-attribution"), 70),
							textX,
							layerAttributionY 
						);
					// </ Add Text>
					
					// Add map image
					pdf.addImage(
						canvas.toDataURL(),
						"JPEG",
						x, y,
						imageWidth, imageHeight
					);
					
					const addNorthArrow = () => {
						return new Promise((resolve) => {
							const s:number = 0.8;
							const w:number = 10 * s;
							const h:number = 19 * s;
							
							pdf.addImage(
								assetURL("assets/images/northarrow.png"),
								"PNG",
								x + imageWidth - w - 2, 
								y + imageHeight - h - 2,
								w, h
							);
							resolve(true);
						});
					}
					const addWatermark = () => {
						return new Promise((resolve) => {
							const s:number = 1;
							const w:number = 20 * s;
							const h:number = 6.4 * s;
							
							pdf.addImage(
								assetURL("assets/images/opusmap-logo-watermark.png"),
								"PNG",
								x + 2, y + 2,
								w, h
							);
							/*
							pdf.setFontSize(4);
							pdf.text(
								limitString(mapConfig.attribution, 70),
								x + 2,
								y + h + 4
							);
							*/
							resolve(true);
						});
					}
					const addScaleLine = () => {
						return new Promise((resolve) => {
							const scaleLineJqueryObj:any = $("#scale-line")?.children?.("div")?.eq?.(0);
							if(!scaleLineJqueryObj || !scaleLineJqueryObj[0]) {
								// No scale line
								console.warn('No scale line. Skipping scale line pdf step.');
								resolve(true);
								return;
							}
							const scaleLineExportOptions:any = {
								width: scaleLineJqueryObj[0].offsetWidth, 
								height: scaleLineJqueryObj[0].offsetHeight,
								backgroundColor: null
							};
							const scaleLineScale:number = 0.25;
							
							const w:number = scaleLineExportOptions.width * scaleLineScale;
							const h:number = scaleLineExportOptions.height * scaleLineScale;
							
							// Capture the scale line bar and add to pdf
							html2canvas(scaleLineJqueryObj[0], scaleLineExportOptions)
							.then((canvas:HTMLCanvasElement) => {
								// Add map image
								pdf.addImage(
									canvas.toDataURL(),
									"PNG",
									x + 1, y + imageHeight - h - 1,
									w, h
								);
								
								resolve(true);
							});
						});
					}
					const completePDF = () => {
						/*
							Completion of PDF generate, download it!
						*/
						const onComplete = () => {
							const finish:Function = () => {
								if(!shouldPrint)
									pdf.save(`${idify(mapConfig.name || "map")}.pdf`);
								else {
									pdf.autoPrint();
									window.open(pdf.output("bloburl"), "_blank");
								}
								
								_ui.hideLoading();
							}
							
							if(getSearchResultsToPrint()?.length > 0) {
								// Add the search result layer info
								addLayerInfo(pdf, finish);
							}else{
								finish();
							}
						}
						
						// No legend wanted, so don't send API request
						if(layerLayout == "none") {
							onComplete();
							return;
						}
						
						/*
							Bounding box request
							API returns the layers visible in the bounding box
						*/
						
						const size:Array<number> = olMap.getSize();
						const extents:Array<number> = olMap.getView().calculateExtent(size);
						const bbox:Array<number> = transformExtent(
							extents,
							mapConfig.epsgCodeView,
							mapConfig.epsgCode
						);
						
						const layersVisible:string = getAllLayerCodesVisible(olMap, true);
						
						// No layers showing, so cut process short
						if(!layersVisible) {
							onComplete();
							return;
						}
						
						const url:string = makeUrl(`https://opus4.co.uk/api/v1/public/layers`, new Map<string, any>([
							["minx", bbox.shift()],
							["miny", bbox.shift()],
							["maxx", bbox.shift()],
							["maxy", bbox.shift()],
							["layers", layersVisible]
						]));
						
						fetchJson(url)
						.then((layers:Array<number>) => {
							if(!layers) layers = [];
							
							addLegend(layers, pdf);
							onComplete();
						});
					}
					
								addScaleLine()
					.then(() => addNorthArrow())
					.then(() => addWatermark())
					.then(() => completePDF());
				});
			});
		}
		const getMapInfoString = ():string => {
			const coords:Array<number> = getMapCoords(olMap);
			const x:string = toPrecision(coords[1], 5)
			const y:string = toPrecision(coords[0], 5)
			const zoom:number = olMap.getView().getZoom();
			
			return `Center: ${x}, ${y}     Zoom: ${zoom}`;
		}
		const getSearchResultsToPrint = ():any[] => {
			const results:any[] = [];
			
			searchResults.forEach((result:any) => {
				if(!result.shouldPrint)
					return;
				
				results.push(result);
			});
			
			return results;
		}
		const getSearchResults = ():any[] => {
			const results:any[] = [];
			
			if(!searchResults)
				return results;
			
			searchResults.forEach((result:any) => {
				results.push(result);
			});
			
			return results;
		}
		const addLayerInfo = (pdf:jsPDF, onComplete:Function) => {
			const results:any[] = getSearchResultsToPrint();
			
			forEachManual(results, (result:any, nextCall:Function) => {
				const opusLayer:OpusLayer = getOpusLayerByCode(result.layerId, olMap);
				
				if(!result)
					return true;
				
				pdf.addPage();
				
				let imageURL:string = opusLayer.images[0];
				const imageSize:number = 15;
				const margin:number = 15;
				
				imageURL = updateURLParameter(imageURL, "width", "40");
				imageURL = updateURLParameter(imageURL, "height", "40");
				
				// Add layer image
				pdf.addImage(
					imageURL,
					"JPEG",
					margin, margin,
					imageSize, imageSize
				);
				
				/* 
					Add search result title
				*/
				pdf.setFontSize(16);
				
				const title:string = result.title || getLocaleString("notitle");
				const splitTitle:string[] = pdf.splitTextToSize(limitString(title, 88), 240);
				const splitOffset:number = (splitTitle.length - 1) * -1.9;
				
				pdf.text(
					splitTitle,
					imageSize + 20, 17 + (imageSize / 2) + splitOffset - 4
				);
				
				/*
					Add layer name
				*/
				pdf.setFontSize(12);
				
				const splitTitle2:string[] = pdf.splitTextToSize(limitString(opusLayer.name, 96), 240);
				const splitOffset2:number = (splitTitle.length - 1) * -1.4;
				
				pdf.text(
					splitTitle2,
					imageSize + 20, 17 + (imageSize / 2) + splitOffset + 2
				);
				
				/*
					Add the meta data
				*/
				let x:number = margin;
				let y:number = margin + imageSize + 5;
				
				// Create a div to contain the layer information
				const div:HTMLElement = document.createElement("div");
				
				// Current page number of the PDF
				const currentPage:number = pdf.internal.pages.length-2;
				
				// The Y offset including the page offset (because jsPDF.html is dumb)
				const yHtml:number = y + (currentPage * pdf.internal.pageSize.getHeight());
				
				// The HTML width for the element
				const htmlWidth:number = pdf.internal.pageSize.getWidth() - 25;
				
				const fixHtml = (str:string):string => {
					if(!str) return str;
					return str += "</b></b>";
				}
				
				/*
					Fields for the layer information
				*/
				const desc:string = fixHtml(result.description) || getLocaleString("nodesc");
				const policies:string = fixHtml(result.policies) || getLocaleString("nopolicies");
				
				div.innerHTML = `<div style='font-size: 3.5px;line-height:4px;font-family: sans-serif;width:${htmlWidth}px;'><p style='font-size:5px'><b>${getLocaleString("description")}</b></p><p>${desc}</p><p style='font-size:5px'><b>${getLocaleString("policies")}</b></p><p>${policies}</p></div>`;
				
				pdf.setFont(undefined, "normal");
				
				pdf.html(
					div,
					{
						x: x, 
						y: yHtml,
						callback: () => {
							nextCall();
						}
					}
				);
			}, 
			() => {
				onComplete();
			});
		}
		const addLegend = (layers:Array<number>, pdf:jsPDF) => {
			// add legend to pdf
			
			const pdfWidth:number = pdf.internal.pageSize.getWidth();
			const pdfHeight:number = pdf.internal.pageSize.getHeight();
			
			/*
				Setup layers
			*/
			const layersUnique:Array<number> = new Array<number>();
				
			for(let layerId of layers) {
				if(layersUnique.indexOf(layerId) > -1) continue;
				
				layersUnique.push(layerId);
			}
			
			/*
				Setup small legend
			*/
			const legendSmall:ExportedLegend = new ExportedLegend({
				width: pdfWidth * 0.45,
				height: footerHeight - marginY - 15,
				layerWidth: 40,
				layerHeight: 6,
				imageSize: 6,
				margin: 5
			});
			
			// Unique decoration for small legend
			legendSmall.onDecorate = function(opusLayer:OpusLayer, layerPos:Vector2) {
				let imageURL:string = opusLayer.images[0];
				
				imageURL = updateURLParameter(imageURL, "width", "25");
				imageURL = updateURLParameter(imageURL, "height", "25");
				
				// Add layer image
				pdf.addImage(
					imageURL,
					"JPEG",
					layerPos.x, layerPos.y,
					this.imageSize, this.imageSize
				);
				
				// Add layer name
				pdf.setFontSize(8);
				
				const splitTitle:string[] = pdf.splitTextToSize(limitString(opusLayer.name, 62), 30);
				const splitOffset:number = (splitTitle.length - 1) * -1.8;
				
				pdf.text(
					splitTitle,
					layerPos.x + this.imageSize + 2, layerPos.y + 4 + splitOffset
				);
			}
			
			/*
				Setup large legend
			*/
			const legendLarge:ExportedLegend = new ExportedLegend({
				width: pdfWidth - (marginX * 2),
				height: pdfHeight - (marginY * 2) - 20,
				layerWidth: 80,
				layerHeight: 12,
				imageSize: 15,
				margin: 5
			});
			
			// Unique decoration for small legend
			legendLarge.onDecorate = function(opusLayer:OpusLayer, layerPos:Vector2) {
				let imageURL:string = opusLayer.images[0];
				
				imageURL = updateURLParameter(imageURL, "width", "40");
				imageURL = updateURLParameter(imageURL, "height", "40");
				
				// Add layer image
				pdf.addImage(
					imageURL,
					"JPEG",
					layerPos.x, layerPos.y,
					this.imageSize, this.imageSize
				);
				
				// Add layer name
				pdf.setFontSize(10);
				
				const splitTitle:string[] = pdf.splitTextToSize(limitString(opusLayer.name, 88), 60);
				const splitOffset:number = (splitTitle.length - 1) * -2.1;
				
				pdf.text(
					splitTitle,
					layerPos.x + this.imageSize + 5, layerPos.y + 8 + splitOffset
				);
			}
			
			/*
				Pos vector will control where each layer goes on the page
			*/
			const pos:Vector2 = new Vector2(0, 0);
			
			/*
				Add layer method according to the legend type
			*/
			const addLayer = (opusLayer:OpusLayer, legend:ExportedLegend) => {
				const layerPos:Vector2 = new Vector2(pos.x + legend.offsetx, pos.y + legend.offsety);
				
				legend.decorate(opusLayer, layerPos);
				
				pos.x += legend.layerWidth + legend.margin;
				
				if(pos.x+legend.layerWidth >= legend.width) {
					pos.x = 0;
					pos.y += legend.layerHeight + legend.margin;
				}
				
				return pos;
			}
			
			
			let layersAdded:number = 0;
			
			switch(layerLayout) {
				case "small": {
					legendSmall.offsetx = pdfWidth - legendSmall.width - marginX;
					legendSmall.offsety = pdfHeight - legendSmall.height - marginY;
					
					/*
						No layers to show, so stop here
					*/
					if(layersUnique.length < 1) {
						pdf.setFontSize(10);
						pdf.text(
							getLocaleString("nolayers"),
							legendSmall.offsetx, legendSmall.offsety + 5
						);
						return;
					}
					
					for(let layerId of layersUnique) {
						const layer:OpusLayer = getOpusLayerByCode(`o:${layerId}`, olMap);
						
						if(!layer) continue;
						
						const pos:Vector2 = addLayer(layer, legendSmall);
						
						layersAdded++;
						
						// Maximum space allowed reached
						if(pos.y >= legendSmall.height && layersUnique.length > layersAdded) {
							pdf.setFontSize(8);
							pdf.text(
								`+${layersUnique.length - layersAdded} ${getLocaleString("more...")}`,
								legendSmall.offsetx + pos.x, legendSmall.offsety + pos.y
							);
							break;
						}
					}
					break;
				}
				case "large": {
					pdf.addPage();
					
					legendLarge.offsetx = marginX;
					legendLarge.offsety = marginY + 20;
					
					let legendPages = 1;
					
					const addLegendTitle = (page:number) => {
						pdf.setFont(undefined, "bold");
						pdf.setFontSize(12);
						pdf.text(
							getLocaleString("legend") + (page == 1 ? ` (${layersUnique.length})` : ""),
							marginX, marginY + 8
						);
						
						pdf.setFont(undefined, "normal");
						pdf.setFontSize(8);
						pdf.text(
							`${getLocaleString("page")} ${page}`,
							marginX, marginY + 12
						);
					}
					
					addLegendTitle(legendPages);
					
					/*
						No layers to show, so stop here
					*/
					if(layersUnique.length < 1) {
						pdf.setFontSize(10);
						pdf.text(
							getLocaleString("nolayers"),
							legendLarge.offsetx, legendLarge.offsety + 5
						);
						return;
					}
					
					for(let layerId of layersUnique) {
						const layer:OpusLayer = getOpusLayerByCode(`o:${layerId}`, olMap);
						
						if(!layer) continue;
						
						const pos:Vector2 = addLayer(layer, legendLarge);
						
						layersAdded++;
						
						// Maximum space allowed reached
						if(pos.y + legendLarge.layerHeight >= legendLarge.height && layersUnique.length > layersAdded) {
							pos.set(0, 0);
							pdf.addPage();
							
							legendPages++;
							addLegendTitle(legendPages);
						}
					}
					break;
				}
			}
		}
		const drawPinDropLayers = () => {
			const rows:any[] = [];
			
			const toggleAllLayers = () => {
				let toggleTo:boolean = !allLayersToggled();
				
				searchResults.forEach((result:any) => {
					result.shouldPrint = toggleTo;
				});
				setSearchResults([...searchResults]);
			}
			const allLayersToggled = ():boolean => {
				let allToggled:boolean = true;
				
				searchResults.forEach((result:any) => {
					if(!result.shouldPrint)
						allToggled = false;
				});
				
				return allToggled;
			}
			
			searchResults.forEach((result:any) => {
				const opusLayer:OpusLayer = getOpusLayerByCode(result.layerId, olMap);
				
				let imageURL:string = opusLayer.images[0];
				
				imageURL = updateURLParameter(imageURL, "width", "20");
				imageURL = updateURLParameter(imageURL, "height", "20");
				
				const toggleLayer = () => {
					result.shouldPrint = !result.shouldPrint;
					setSearchResults([...searchResults]);
					
					_ui.tooltip.hide();
				}
				
				const fullName:string = `${(result.title || getLocaleString("notitle"))} (${opusLayer.name})`;
				
				rows.push(
					<div 
						className="print-pin-drop-row" 
						key={rows.length} 
						data-tip={fullName}
						data-delay-show="700"
					>
						<div className="filter-field--option prevent-select" onClick={() => toggleLayer()}>
							<input
								type="checkbox"
								checked={result.shouldPrint}
								onChange={() => {}}
							/> 
							
							<img src={imageURL} />
							
							<div className="print-pin-drop-row__meta">
								{limitString(fullName, 46)}
							</div>
						</div>
					</div>
				);
			});
			
			return (
				<div className="print-pin-drop-container">
					<div className="ib">
						<img src={assetURL(markerIconSource)} className="print-pin-icon" /> 
						<b>{getLocaleString("pindroplayersili")}</b>
					</div>
					<div className="ib print-check-all">
						<div className="filter-field--option prevent-select" onClick={() => toggleAllLayers()}>
							<input
								type="checkbox"
								checked={allLayersToggled()}
								onChange={() => {}}
							/> 
							{getLocaleString("checkall")}
						</div>
					</div>
					
					<div className="print-pin-drop-rows">
						{rows}
					</div>
				</div>
			)
		}
		
		return (
			<>
				<p><b>{getLocaleString("exportoptions")}</b></p>
				
				<div className="print-export-option">
					{getLocaleString("pagesize")}
					<select onChange={(e:any) => setPageSize(e.target.value)} defaultValue="a4">
						<option value="a0">A0</option>
						<option value="a1">A1</option>
						<option value="a2">A2</option>
						<option value="a3">A3</option>
						<option value="a4">A4</option>
						<option value="a5">A5</option>
					</select>
				</div>
				<div className="print-export-option">
					{getLocaleString("displaylegend")}
					<select onChange={(e:any) => setLayerLayout(e.target.value)} defaultValue="small">
						{
							pageSize != "a5" ? (<option value="small">{getLocaleString("pdflegendsmall")}</option>) : null
						}
						<option value="large">{getLocaleString("pdflegendlarge")}</option>
						<option value="none">{getLocaleString("pdflegendnone")}</option>
					</select>
				</div>
				
				{getSearchResults().length > 0 && drawPinDropLayers()}
				
				<hr />
				
				<p>{getLocaleString("exportlabel")}</p>
				
				<button onClick={() => exportPDF(true)} style={{marginRight: 10}}>
					<img src={assetURL("assets/images/print.png")} height='20' style={{verticalAlign: "middle", paddingRight: 10}} />
					{getLocaleString("printpdf")}
				</button>
				<button onClick={() => exportPDF(false)}>
					<img src={assetURL("assets/images/pdf.png")} height='20' style={{verticalAlign: "middle", paddingRight: 10}} />
					{getLocaleString("exportpdf")}
				</button>
			</>
		)
	}
});

export default PrintPopup;